import React, { useEffect, useRef, useState } from "react";
import { ArrowCounterClockwise, Play, Pause } from "@phosphor-icons/react";

export const MatrixRain: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    const chars = "01010101ABCDEFGHIJKLMNOPQRSTUVWXYZｦｱｳｴｵｶｷｹｺｻｼｽｾｿﾀﾂﾃﾅﾆﾇﾈﾊﾋﾎﾏﾐﾑﾒﾓﾔﾕﾗﾘﾜ";
    const fontSize = 14;

    const resize = () => {
      canvas.width = canvas.parentElement?.clientWidth || 640;
      canvas.height = 360;
    };
    resize();

    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.floor(Math.random() * -50);
    }

    let lastTime = 0;
    const fps = 30;
    const interval = 1000 / fps;

    const render = (time: number) => {
      animationFrameId = requestAnimationFrame(render);
      if (!isRunning) return;

      const delta = time - lastTime;
      if (delta < interval) return;
      lastTime = time - (delta % interval);

      // Semi-transparent background to give fading trail
      ctx.fillStyle = "rgba(18, 17, 16, 0.12)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Bright tip
        ctx.fillStyle = "#FFFFFF";
        ctx.fillText(text, x, y);

        // Amber trail behind
        ctx.fillStyle = "#E5983A";
        ctx.fillText(text, x, y - fontSize);

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };

    animationFrameId = requestAnimationFrame(render);

    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resize);
    };
  }, [isRunning]);

  return (
    <div className="flex flex-col h-full select-none relative">
      <div className="flex items-center justify-between px-4 py-2 bg-[#171615] border-b border-[#242320] text-xs font-mono">
        <div className="flex items-center gap-2">
          <span className="text-[#E5983A] font-bold">matrix.sh</span>
          <span className="text-[#66645E]">--stream active</span>
        </div>

        <button
          onClick={() => setIsRunning(!isRunning)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#22211F] hover:bg-[#2C2A26] border border-[#2F2D29] text-[11px] text-[#DCD8CE] hover:text-[#E5983A] transition cursor-pointer"
        >
          {isRunning ? (
            <>
              <Pause size={12} weight="fill" />
              <span>Pause</span>
            </>
          ) : (
            <>
              <Play size={12} weight="fill" />
              <span>Resume</span>
            </>
          )}
        </button>
      </div>

      <div className="w-full h-[360px] bg-[#121110] overflow-hidden">
        <canvas ref={canvasRef} className="w-full h-full block" />
      </div>

      <div className="px-4 py-2 bg-[#141413] border-t border-[#242320] flex items-center justify-between text-[11px] font-mono text-[#787670]">
        <span>Rendered via HTML5 Canvas Monospace Buffer</span>
        <span className="text-[#E5983A]">Amber Phosphor // Katakana + ASCII</span>
      </div>
    </div>
  );
};
